import clsx from "clsx";
import { RadioGroup as RadioGroupPrimitive } from "radix-ui";
import React from "react";
import styles from "./radio-group.module.scss";

// Define the props for the RadioGroup component itself using a type alias
export type RadioGroupProps = React.ComponentPropsWithoutRef<
  typeof RadioGroupPrimitive.Root
>;
// Add custom props by intersection if needed later:
// export type RadioGroupProps = React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root> & { customProp?: string };

// Define the props for individual RadioGroup items
export interface RadioGroupItemProps
  extends React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item> {
  label: string; // Require a label for accessibility
}

// Export the main RadioGroup component
export const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  RadioGroupProps
>(({ className, children, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Root
      className={clsx(styles.root, className)}
      ref={ref}
      {...props}
    >
      {children}
    </RadioGroupPrimitive.Root>
  );
});
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

// Export the RadioGroupItem component
export const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  RadioGroupItemProps
>(({ className, label, id: idProp, ...props }, ref) => {
  const generatedId = React.useId();
  const itemId = idProp || generatedId;

  return (
    <div className={styles.itemContainer}>
      <RadioGroupPrimitive.Item
        ref={ref}
        className={clsx(styles.item, className)}
        id={itemId}
        {...props}
      >
        <RadioGroupPrimitive.Indicator className={styles.indicator} />
      </RadioGroupPrimitive.Item>
      <label htmlFor={itemId} className={styles.label}>
        {label}
      </label>
    </div>
  );
});
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;
